import { FeatureHelperService } from './../_helpers/feature-helper/feature-helper.service';
import { hasRole } from '../_helpers/can-use/current-user-roles';
import { AfterContentInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BaseComponent } from '../base.component';
import { ThemeService } from '../_helpers/theming-engine/theme.service';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { FeedbackModalComponent } from '../student-voice/feedback-modal/feedback-modal.component';
import { environment } from '../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { PlacementService } from '../_services';
import pkg from '../../../package.json';

/**
 * The role features the side menu reads; getAccess() sets the property of the same name from each.
 * page-gates.spec.ts fails when one has no Check access entry in PageGates.
 */
export const NavMenuFeatures: readonly string[] = [
  "TutorHomeView",
  "CoordinatorHomeView",
  "ClientHomeView",
  "MissingSubmissionsView",
  "StudentSessionsView",
  "ActionsView",
  "SessionSearchView",
  "TcDashboardView",
  "ClientAdminEdit",
  "AccountSettingsEdit",
  "ReportsView",
  "QaReportApprovalView",
  "BookingSearchView",
  "StudentDataSearch",
  "StudentDataInsert",
  "Student",
  "SessionOverviewView",
  "EmailStudentAccessLink",
  "ViewStudentFeedbackDashboard",
  "OnlineOverviewClient",
  "OnlineOverviewAdmin",
  "UserMaintenanceView",
  "UserMaintenanceEdit",
  "RoleFeatureAdmin",
  "ServiceProcessAdmin",
  "FeatureSwitchAdmin",
  "EmailTemplatesView",
  "EmailHistoryView",
  "LiveActivityView"
];

export interface HomeLink { label: string; route: string; }

/** The landing pages the given home-feature flags open, in menu order, each at most once. */
export function homeLinksFor(flags: {
  Student: boolean; ClientHomeView: boolean; TutorHomeView: boolean; CoordinatorHomeView: boolean;
}): HomeLink[] {
  const links: HomeLink[] = [];
  if (flags.Student) { links.push({ label: 'Learner Home', route: '/student-home' }); }
  if (flags.ClientHomeView) { links.push({ label: 'Client Home', route: '/clientlanding' }); }
  if (flags.TutorHomeView) { links.push({ label: 'Tutor Home', route: '/tutorlanding' }); }
  if (flags.CoordinatorHomeView) { links.push({ label: 'Tutor Co-ordinator Home', route: '/coordinatorlanding' }); }
  return links;
}

@Component({
    selector: 'nav-menu',
    templateUrl: './nav-menu.component.html',
    styleUrls: ['./nav-menu.component.css'],
    standalone: false
})
export class NavMenuComponent extends BaseComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild("sidebar", {static: false}) sidebar: ElementRef;

  private sidebarToggled: boolean = false;
  private navigationSubscription: Subscription | null = null;

  public TutorHomeView: boolean = false;
  public CoordinatorHomeView: boolean = false;
  public ClientHomeView: boolean = false;
  public MissingSubmissionsView: boolean = false;
  public StudentSessionsView: boolean = false;
  public ActionsView: boolean = false;
  public SessionSearchView: boolean = false;
  public TcDashboardView: boolean = false;
  public ClientAdminEdit: boolean = false;
  public AccountSettingsEdit: boolean = false;
  public ReportsView: boolean = false;
  public QaReportApprovalView: boolean = false;
  public BookingSearchView: boolean = false;
  public StudentDataSearch: boolean = false;
  public StudentDataInsert: boolean = false;
  public Student: boolean = false;
  public SessionOverviewView: boolean = false;
  public isAdmin: boolean = false;
  public EmailStudentAccessLink: boolean = false;
  public ViewStudentFeedbackDashboard: boolean = false;
  public OnlineOverviewClient: boolean = false;
  public OnlineOverviewAdmin: boolean = false;
  public UserMaintenanceView: boolean = false;
  public UserMaintenanceEdit: boolean = false;
  public RoleFeatureAdmin: boolean = false;
  public ServiceProcessAdmin: boolean = false;
  public FeatureSwitchAdmin: boolean = false;
  public EmailTemplatesView: boolean = false;
  public EmailHistoryView: boolean = false;
  public LiveActivityView: boolean = false;

  public homeLinks: HomeLink[] = [];

  public homeToggled: boolean = false;
  public sessionsToggled: boolean = false;
  public managementToggled: boolean = false;
  public usersToggled: boolean = false;
  public studentsToggled: boolean = false;
  public adminToggled: boolean = false;


  public currentUserInitials: string = '';
  public currentUserName: string = '';
  public currentUserEmail: string = '';
  public currentUserRole: string = '';

  public appVersion: string = 'V' + pkg.version;
  public currentYear: number = new Date().getFullYear();

  // Standard external links for tutors/clients — replaces the removed fat footer.
  public readonly safeguardingUrl =
    'https://form.thesafeguardingcompany.com/aee7f402-6072-477f-a94e-cd636d5eaf14';
  public readonly footerLinks: { label: string; url: string }[] = [
    { label: 'Cookie policy',         url: 'https://assets.teachingpersonnel.com/pdf/LEAP_CookiePolicy.pdf' },
    { label: 'Privacy policy',        url: 'https://www.teachingpersonnel.com/privacy-notice' },
    { label: 'Client privacy policy', url: 'https://assets.teachingpersonnel.com/pdf/privacynoticeclients.pdf' },
    // TODO: Gleap help centre — URL/integration pending; renders as a disabled placeholder until provided.
    //{ label: 'Help centre',           url: '' },
  ];

  public sidebarCollapsed: boolean = false;

  public mobileWindow: boolean = (window.innerWidth) <= 530;
  @HostListener('window:resize')
  checkingForMobileSize() {
    this.mobileWindow = (window.innerWidth) <= 530;
  }

  constructor(
    public router: Router,
    public themeService: ThemeService,
    public errorModalHelper: ErrorModalHelperService,
    private featureHelperService: FeatureHelperService,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    public dialog: MatDialog,
    private placementService: PlacementService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
    const user = this.authService.currentUserValue;
    if (user) {
      this.currentUserInitials = user.firstName.slice(0, 1) + user.lastName.slice(0, 1);
      this.currentUserName = user.firstName + ' ' + user.lastName;
      this.currentUserEmail = user.email;
      // `isStaff` means "holds the STAFF role code", and STAFF is the TUTOR role in this system
      // (UserService.GetTutorUsers selects tutors by it; DigitalClassroom joins a STAFF user to a room
      // as the tutor). This label previously read "Staff" for tutors and "User" for everyone else,
      // including admins — the opposite of what it looked like it said.
      this.currentUserRole = user.isStaff ? 'Tutor' : 'User';
    }
  }

  ngOnInit() {
    setTimeout(() => {
      this.getAccess();
      // The permission flags gate @if blocks in the template; run a clean check pass immediately so
      // the menu updates in this tick rather than a later one (guards against NG0100 under
      // event-coalescing or an aborted earlier pass).
      this.changeDetectorRef.detectChanges();
    }, 200);

    // The menu outlives every page, so it re-reads its flags after each navigation. AuthGuard has refreshed the
    // role features from the server by then, and each *appFeatureSwitch item asks again for its switch.
    this.navigationSubscription = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        this.getAccess();
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();
  }

  ngAfterContentInit(): void {
    setTimeout(() => {
      this.checkingForMobileSize();
    }, 500);
  }

  getAccess() {
    let featureList = [...NavMenuFeatures];

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);
    featureList.forEach(feature => {
      let userFeatureIndex = currentUserFeatures.findIndex(f => f.feature.toLowerCase() === feature.toLowerCase());
      if (userFeatureIndex > -1) {
        this[feature] = currentUserFeatures[userFeatureIndex].enabled;
      }
    });

    // System Controls, New Features and Role Features are admin-only: the grant alone does not show them.
    const admin = hasRole('ADMIN');
    this.ServiceProcessAdmin = this.ServiceProcessAdmin && admin;
    this.FeatureSwitchAdmin = this.FeatureSwitchAdmin && admin;
    this.RoleFeatureAdmin = this.RoleFeatureAdmin && admin;

    this.isAdmin = this.Student && this.ClientHomeView && this.TutorHomeView && this.CoordinatorHomeView;
    this.refreshHomeLinks();
  }

  /**
   * The home pages this user may open, one entry per landing page. The menu shows a single "Home" link
   * when there is one, and a "Home" group naming each page when there are more.
   *
   * This used to render one unlabelled "Home" per home feature unless the user held all four. The ADMIN
   * role holds Client, Tutor and Co-ordinator home but not the learner-only Student feature, so an admin
   * saw "Home" three times, each going somewhere different.
   */
  public refreshHomeLinks(): void {
    this.homeLinks = homeLinksFor(this);
  }

  public test(): void {
    const dialogRef = this.dialog.open(FeedbackModalComponent, {
      data: { modalHeader: "Session Feedback", studentId: 123},
      panelClass: 'my-dialog',
      disableClose: true
    });
  }

  testErrorModal(): void {
    this.errorModalHelper.show("Error or Validation Error Test", { status: 401 });
  }

  public toggleSidebar(): void {
    if (!this.mobileWindow) return;

    this.sidebarToggled = !this.sidebarToggled;
    if (this.sidebarToggled) {
      this.renderer.addClass(this.sidebar.nativeElement, "sidenav--toggled");
    } else {
      this.renderer.removeClass(this.sidebar.nativeElement, "sidenav--toggled");
    }
  }

  public routeActivated(): void {
    if (this.mobileWindow && this.sidebarToggled) {
      this.sidebarToggled = false;
      this.renderer.removeClass(this.sidebar.nativeElement, "sidenav--toggled");
    }
  }

  public toggleMenuSelect(propertyName: string): void {
    let currentPropertyValue = this[propertyName];
    this[propertyName] = !currentPropertyValue;
  }

  public toggleCollapse(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  public logout(): void {
    this.router.navigate(["/login"]);
  }
}
